import {
  Component,
  OnInit,
  OnDestroy,
  ViewChild,
  ElementRef,
  LOCALE_ID,
  computed,
  inject,
  signal,
  DestroyRef,
} from "@angular/core";
import { Observable } from "rxjs";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { LayoutBreakpointService } from '@app/core/layout/layout-breakpoint.service';
import { MatDrawer } from "@angular/material/sidenav";
import { MatSelect } from "@angular/material/select";
import { Router } from "@angular/router";
import { ActivatedRoute } from "@angular/router";
import { Title } from "@angular/platform-browser";

import { StoreAuthService } from '@app/core/auth/store-auth.service';
import { LogoutService } from '@app/core/auth/logout.service';
import { ListUsersService } from "../../../data/user/list-users.service";
import { GetMemberService } from "../../../data/member/get-member.service";
import {
  DatabaseCacheService,
  ContractInfo,
  Section,
  UserData,
} from "../../../data/cache/database-cache.service";
import { ListSectionsService } from "../../../data/section/list-sections.service";
import { el } from "date-fns/locale";
import { SHARED_IMPORTS } from "src/app/shared/standalone-imports";
import { ThemeMode, ThemeService } from '@app/core/layout/theme.service';
import { AppHeaderComponent } from "src/app/shared/components/app-header/app-header.component";
import { MatBottomSheet } from "@angular/material/bottom-sheet";
import { SectionSwitchBottomSheetComponent, SectionSwitchItem } from "../../home/section-switch-bottom-sheet/section-switch-bottom-sheet.component";

@Component({
  selector: "admin-nav",
  templateUrl: "./admin-nav.component.html",
  styleUrls: ["./admin-nav.component.css"],
  imports: [...SHARED_IMPORTS, AppHeaderComponent],
})
export class AdminNavComponent implements OnInit, OnDestroy {
  @ViewChild("drawer") drawer: MatDrawer;
  @ViewChild("sectionSelect") sectionSelect: MatSelect;

  private readonly destroyRef = inject(DestroyRef);
  private readonly themeService = inject(ThemeService);
  private readonly isEnglish = (inject(LOCALE_ID) || '').toLowerCase().startsWith('en');
  private readonly logoutService = inject(LogoutService);

  // 変数
  error: any;
  searchText: any;
  responseData: any = [];
  userPlaceHolder: any = { user: { code: "--", lastName: "", firstName: "" } };
  confirmedSelectedSection: Section = null;
  sections;
  year = new Date().getFullYear();

  contractEnabled: boolean = false;
  selectedSectionGroup: any;
  selectedSectionGroupAssets: Record<string, any>;
  selectedSectionGroupFunctions: string[];
  accountManagementRole: boolean = false;
  userManagementRole: boolean = false;

  sectionIdInUrl;
  selectedSection: any;
  public selectedSectionGroupUserDataAttributes: UserData[];
  private functionsOrder = [
    "UserData",
    "RecordList",
    "Dialog",
    "Video",
    "GOST",
  ];

  public loginUser = { loginUserName: "", isLoggedIn: false };
  loginUserJob: string = "";
  toolbarTitle: string = "";
  IsNavSystemOpen: boolean = true;
  isHandset$: Observable<boolean>;
  private isHandset = false;
  readonly isSidenavCollapsed = signal(false);
  isSectionSelectExpanded = false;
  private readonly NARROW_WIDTH_BREAKPOINT = 768;
  // Pending timer that opens the section selector panel after the trigger
  // expands (mobile). Tracked so it can be cancelled and never re-opens the
  // panel after a selection / close.
  private sectionSelectOpenTimer: ReturnType<typeof setTimeout> | null = null;
  urlSelectedSectionId: string;
  readonly themeIcon = computed(() => {
    const mode = this.themeService.mode();
    if (mode === "system") return "brightness_auto";
    return mode === "dark" ? "dark_mode" : "light_mode";
  });
  readonly themeLabel = computed(() => {
    const mode = this.themeService.mode();
    if (mode === "light") return this.isEnglish ? "Switch to dark mode" : "ダークモードに切替";
    if (mode === "dark") return this.isEnglish ? "Switch to system mode" : "システム設定に切替";
    return this.isEnglish ? "Switch to light mode" : "ライトモードに切替";
  });

  sectionGroupAdminRole;
  systemAdminRole;
  distributorAgentRole;
  administeredSectionGroupId;

  yearOfPrevMonth;
  monthOfPrevMonth;

  private readonly layoutBreakpoint = inject(LayoutBreakpointService);
  private readonly bottomSheet = inject(MatBottomSheet);

  constructor(
    private storeAuth: StoreAuthService,
    private router: Router,
    public common: DatabaseCacheService,
    private titleService: Title,
    private route: ActivatedRoute,
    private listSectionsService: ListSectionsService,
  ) {}

  callFullscreen() {
    if (!document.fullscreenElement) {
      if (document.documentElement.requestFullscreen) {
        document.documentElement.requestFullscreen();
      }
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen();
      }
    }
  }

  toggleTheme(): void {
    const modes: ThemeMode[] = ["light", "dark", "system"];
    const current = this.themeService.mode();
    const nextIndex = (modes.indexOf(current) + 1) % modes.length;
    this.themeService.setTheme(modes[nextIndex]);
  }

  private updateLoginUserJob(): void {
    const selectedSectionWithRole = this.common.getSelectedSectionWithRole();
    if (!selectedSectionWithRole) {
      this.loginUserJob = "";
      return;
    }
    const jobTitleCode = this.common.getSelectedSectionJobTitleCode();
    this.loginUserJob = this.common.getJobTitle(jobTitleCode);
  }

  async ngOnInit() {
    this.isHandset$ = this.layoutBreakpoint.isHandset$;

    // Track handset state for toggleSelf behavior
    this.isHandset$.pipe(takeUntilDestroyed(this.destroyRef)).subscribe((isHandset) => {
      this.isHandset = isHandset;
    });

    // Auto-collapse sidenav when entering tablet mode
    this.layoutBreakpoint.isTablet$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isTablet) => {
        if (isTablet) {
          this.isSidenavCollapsed.set(true);
        }
      });

    this.setPrevMonth();

    // if (this.common.getSelectedSection() == null){
    //   await this.getUseritself();
    // }

    this.storeAuth
      .getLoginUserNameObservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((name) => {
        this.loginUser.loginUserName = name || "";
      });
    this.storeAuth
      .getIsLoggedInOservable()
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((isLoggedIn) => {
        this.loginUser.isLoggedIn = isLoggedIn;
      });

    this.selectedSection = this.common.getSelectedSection() ?? {};
    this.updateLoginUserJob();

    this.listSections();

    // section Group の初期化
    // this.selectedSectionGroupAssets =  this.common.getSelectedSectionGroupAssets();
    // this.selectedSectionGroupFunctions =  this.common.getSelectedSectionGroupFunctions();
    // this.selectedSectionGroupFunctions.sort((a, b) => this.functionsOrder.indexOf(a) - this.functionsOrder.indexOf(b));
    this.accountManagementRole = false;
    this.userManagementRole = false;

    this.common.parametersUpdated
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((noData) => {
      // SectionGroupに関わるデータの読み込み
      this.selectedSectionGroup = this.common.getSelectedSectionGroup();
      this.selectedSectionGroupAssets =
        this.selectedSectionGroup.settings.assets;
      this.selectedSectionGroupFunctions =
        this.selectedSectionGroup.settings.functionAttributes;
      this.selectedSectionGroupFunctions.sort(
        (a, b) =>
          this.functionsOrder.indexOf(a) - this.functionsOrder.indexOf(b),
      );

      this.updateLoginUserJob();

      this.titleService.setTitle(
        this.selectedSectionGroup.name + " Cloud Admin",
      );

      // Sectionに関わるデータの読み込み
      this.selectedSection = this.common.getSelectedSection();

      this.contractEnabled = this.common.getSelectedSectionContractEnabled();

      // 権限の設定
      this.determineRoles(this.common.getSelectedSectionRoles());
    });

    // route.params.subscribe への Subscribe は this.common.parametersUpdated への Subscribe よりあとに行うこと
    // section-admin/:sectionId のルートから sectionId を取得する
    this.route.paramMap
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((params) => {
      this.setPrevMonth();

      this.sectionIdInUrl = params.get("sectionId"); // sectionId in the URL pass
      if (this.sectionIdInUrl != null) {
        this.common.setSelectedSectionById(this.sectionIdInUrl);
      } else {
        this.common.setSelectedSectionById(
          this.common.getSelectedSection().sectionId,
        );
      }

      // Sectionに関わるデータの読み込み
      this.selectedSection = this.common.getSelectedSection();

      this.contractEnabled = this.common.getSelectedSectionContractEnabled();

      // 権限の設定
      this.determineRoles(this.common.getSelectedSectionRoles());
    });
  }

  determineRoles(roles) {
    this.accountManagementRole = false;
    this.userManagementRole = false;

    if (roles != null && roles.length != 0) {
      if (roles.indexOf("ACCOUNT_MANAGEMENT") !== -1) {
        this.accountManagementRole = true;
      }

      if (roles.indexOf("USER_MANAGEMENT") !== -1) {
        this.userManagementRole = true;
      }
    } else {
    }

    this.sectionGroupAdminRole = false;
    try {
      // 現在選択されているSectionの親のSectionGroupの管理者権限を持っているかチェック
      let sections = this.common.getSectionDocuments();
      let selectedSection = sections.find(
        (s) => s.sectionId == this.selectedSection.sectionId,
      );
      let sectionGroups = this.common.getSectionGroupDocuments();
      let selectedSectionGroup = sectionGroups.find(
        (s) => s.id == selectedSection.sectionGroupId,
      );
      if (selectedSectionGroup) {
        this.selectedSectionGroup = selectedSectionGroup;
      }
      let sectionGroupAdmins = this.common.getSectionGroupAdmins();
      let selectedSectionGroupAdmin = sectionGroupAdmins.find(
        (s) => s.sectionGroupId == selectedSectionGroup.id,
      );
  
      // if (selectedSectionGroupAdmin != null) {
      //   this.sectionGroupAdminRole = true;
      // }
    } catch {
      // do nothing
    }

    try{
      if (this.common.hasAllSectionGroupManagement()) {
        this.administeredSectionGroupId = this.common.getSectionGroupDocuments()?.[0]?.id ?? null;
      } 
      else
      {
        let administeredSectionGroups = this.common.getSectionGroupAdmins();
        if (administeredSectionGroups != null && administeredSectionGroups.length > 0) {
          this.administeredSectionGroupId = administeredSectionGroups[0].sectionGroupId;
        }
      }    
    }
    catch{
      // do nothing 
    }

    this.systemAdminRole = false;
    try {
      let systemAdmin = this.common.getSystemAdmin();
      if (systemAdmin != null) {
        this.systemAdminRole = true;

        if (
          systemAdmin.roles.find((r) => r == "ALL_SECTION_GROUP_MANAGEMENT")
        ) {
          this.sectionGroupAdminRole = true; // SystemAdminとして すべての SectionGroupの管理権限を持っている
        }
      }
    } catch {
      // do nothing
    }

    this.distributorAgentRole = this.common.hasAnyDistributorAgentRole();
  }

  login() {
  }

  logout() {
    this.logoutService.logout();
  }

  onSelectionChange(event: any) {
  }


  setPrevMonth() {
    this.yearOfPrevMonth = new Date().getFullYear();
    this.monthOfPrevMonth = new Date().getMonth();

    if (this.monthOfPrevMonth == 0) {
      this.monthOfPrevMonth = 12;
      this.yearOfPrevMonth--;
    }
  }

  toggleSelf() {
    if (this.isHandset) {
      // Mobile: toggle drawer (overlay mode only)
      this.drawer?.toggle();
    } else {
      // Tablet/Desktop: toggle collapsed state
      this.isSidenavCollapsed.update(v => !v);
    }
  }

  public sectionComparison = (option: any, value: any): boolean => {
    return option?.id === value?.sectionId || option?.sectionId === value?.sectionId;
  };

  listSections() {
    let allSections = this.common.getSectionDocuments();
    let sectionWithRoles = this.common.getSectionWithRole();

    this.sections = [];
    for (var i = 0; i < sectionWithRoles.length; i++) {
      let sectionWithRole = sectionWithRoles[i];
      let roles = sectionWithRole.permissions.role;
      if (
        roles.indexOf("ACCOUNT_MANAGEMENT") != -1 ||
        roles.indexOf("USER_MANAGEMENT") != -1
      ) {
        let section = allSections.find(
          (s) => s.id == sectionWithRole.sectionId,
        );
        if (section != null) {
          this.sections.push(section);
        }
      }
    }

  }

  switchSection(event: any) {
    // Cancel any pending programmatic open and force the panel closed so the
    // custom mobile open path can't race Material's close and leave it open.
    this.cancelSectionSelectOpen();
    this.sectionSelect?.close();
    this.isSectionSelectExpanded = false;

    const section = event.value;
    const sectionId = section?.id || section?.sectionId;
    if (sectionId === this.sectionIdInUrl) {
      return; // 現在のSectionが再選択されただけなのでなにもせず終了
    }

    this.router.navigate(["section-admin", sectionId, "home"]);
  }

  /** Close panel on trigger pointerdown if already open (before mat-select processes click) */
  onTriggerPointerDown(event: PointerEvent): void {
    if (this.sectionSelect?.panelOpen) {
      event.preventDefault();
      event.stopPropagation();
      this.sectionSelect.close();
    }
  }

  /**
   * Expand trigger on mousedown (before click opens the panel)
   * For narrow widths, prevent default and open panel after delay
   * This ensures panel position is calculated after trigger is fully expanded
   */
  onSectionSelectMousedown(event: Event): void {
    // Mobile (handset): use a bottom sheet instead of the mat-select dropdown,
    // which is hard to operate on small screens (home-nav と同様の挙動)。
    if (this.isHandset) {
      event.preventDefault();
      event.stopPropagation();
      this.sectionSelect?.close();
      this.openSectionSwitchBottomSheet();
      return;
    }

    if (window.innerWidth <= this.NARROW_WIDTH_BREAKPOINT) {
      // Prevent default mat-select click behavior
      event.preventDefault();
      event.stopPropagation();

      // Expand trigger first
      this.isSectionSelectExpanded = true;

      // Wait for DOM to update, then open panel. Track the timer so a pending
      // open can be cancelled (e.g. on selection) and never stacks.
      this.cancelSectionSelectOpen();
      this.sectionSelectOpenTimer = setTimeout(() => {
        this.sectionSelectOpenTimer = null;
        this.sectionSelect?.open();
      }, 50);
    }
  }

  /** Open section switch bottom sheet (mobile) */
  private openSectionSwitchBottomSheet(): void {
    const sections = this.sections ?? [];
    if (sections.length === 0) return;

    const currentSectionId = this.sectionIdInUrl ?? null;
    const sheetRef = this.bottomSheet.open(SectionSwitchBottomSheetComponent, {
      data: {
        sections: sections.map((s: any) => ({ id: s.id ?? s.sectionId, name: s.sectionName })),
        currentSectionId,
      },
      panelClass: 'section-switch-bottom-sheet-panel',
    });

    sheetRef.afterDismissed().subscribe((item: SectionSwitchItem | undefined) => {
      if (!item || item.id === currentSectionId) return;
      this.router.navigate(["section-admin", item.id, "home"]);
    });
  }

  /** Cancel any pending programmatic open of the section selector panel */
  private cancelSectionSelectOpen(): void {
    if (this.sectionSelectOpenTimer) {
      clearTimeout(this.sectionSelectOpenTimer);
      this.sectionSelectOpenTimer = null;
    }
  }

  /**
   * Collapse trigger when panel closes
   */
  onSectionSelectClosed(): void {
    this.cancelSectionSelectOpen();
    this.isSectionSelectExpanded = false;
  }

  ngOnDestroy(): void {
    this.cancelSectionSelectOpen();
  }

  openNewWindow(ruterurl) {
    // Converts the route into a string that can be used
    // with the window.open() function
    const url = this.router.serializeUrl(this.router.createUrlTree([ruterurl]));
    window.open("#" + url, "_blank");
  }

  async getUseritself(): Promise<void> {
    try {
      this.urlSelectedSectionId = this.route.snapshot.paramMap.get("sectionId"); // sectionId in the URL pass
      const responseData = await this.common.setLoginUserAndSelectedSection(
        this.urlSelectedSectionId,
      );
      if (responseData.sectionWithRole) {
        // When logged-in user belongs to any section

        let roleIndex = 0;
        roleIndex = responseData.sectionWithRole.findIndex(
          (sectionWithRole) =>
            sectionWithRole.sectionId === this.urlSelectedSectionId,
        );
        if (roleIndex == -1) {
          roleIndex = 0;
        }
      }
    } catch (error) {
    }
  }
}
